import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CryptoJS from 'crypto-js';

export const APP_SECRET_KEY = 'cl1930474849@#@$@@^@^';

// Utility function to extract error message from various error formats
// Utility function to extract error message from various error formats
export const extractErrorMessage = (error) => {
  if (!error) {
    return 'An unexpected error occurred';
  }

  // ======================================================
  // NETWORK / INTERNET ERROR HANDLING
  // ======================================================

  // Browser is completely offline
  if (
    typeof navigator !== 'undefined' &&
    navigator.onLine === false
  ) {
    return 'No internet connection. Please check your network and try again.';
  }

  // Handle string network errors before returning the string directly
  if (typeof error === 'string') {
    const message = error.trim();

    if (
      message === 'Network Error' ||
      message.includes('ERR_NETWORK') ||
      message.includes('Failed to fetch') ||
      message.includes('Network request failed')
    ) {
      return 'Unable to connect to CloudVantage. Please check your internet connection and try again.';
    }

    return (
      message ||
      'An unexpected error occurred'
    );
  }

  // Axios/network request failed without receiving a backend response
  if (
    error?.code === 'ERR_NETWORK' ||
    error?.message === 'Network Error' ||
    error?.message?.includes?.('Failed to fetch') ||
    error?.message?.includes?.('Network request failed') ||
    (error?.request && !error?.response)
  ) {
    return 'Unable to connect to CloudVantage. Please check your internet connection and try again.';
  }

  // ======================================================
  // ERROR OBJECT
  // ======================================================

  // If it's an Error object (like axios errors), check response.data first
  if (error instanceof Error) {
    // Axios errors have a response property - check for statusmessage first
    if (error.response && error.response.data) {
      if (
        error.response.data.statusmessage &&
        typeof error.response.data.statusmessage === 'string' &&
        error.response.data.statusmessage.trim()
      ) {
        return error.response.data.statusmessage.trim();
      }

      if (
        error.response.data.message &&
        typeof error.response.data.message === 'string' &&
        error.response.data.message.trim()
      ) {
        return error.response.data.message.trim();
      }
    }

    // If no backend response/message was found
    return (
      error.message ||
      'An unexpected error occurred'
    );
  }

  // ======================================================
  // PLAIN OBJECT ERROR
  // ======================================================

  if (typeof error === 'object') {
    // PRIORITY 1:
    // Direct API response with statuscode/statusmessage
    if (error.statuscode !== undefined) {
      if (
        error.statusmessage &&
        typeof error.statusmessage === 'string' &&
        error.statusmessage.trim()
      ) {
        return error.statusmessage.trim();
      }
    }

    // PRIORITY 2:
    // Nested response.data
    if (error.response && error.response.data) {
      if (
        error.response.data.statusmessage &&
        typeof error.response.data.statusmessage === 'string' &&
        error.response.data.statusmessage.trim()
      ) {
        return error.response.data.statusmessage.trim();
      }

      if (
        error.response.data.statuscode !== undefined &&
        error.response.data.statusmessage &&
        typeof error.response.data.statusmessage === 'string' &&
        error.response.data.statusmessage.trim()
      ) {
        return error.response.data.statusmessage.trim();
      }
    }

    // PRIORITY 3:
    // data property
    if (error.data) {
      if (
        error.data.statuscode !== undefined &&
        error.data.statusmessage &&
        typeof error.data.statusmessage === 'string' &&
        error.data.statusmessage.trim()
      ) {
        return error.data.statusmessage.trim();
      }
    }

    // PRIORITY 4:
    // payload.data
    if (error.payload && error.payload.data) {
      if (
        error.payload.data.statuscode !== undefined &&
        error.payload.data.statusmessage &&
        typeof error.payload.data.statusmessage === 'string' &&
        error.payload.data.statusmessage.trim()
      ) {
        return error.payload.data.statusmessage.trim();
      }
    }

    // PRIORITY 5:
    // Common error message properties
    const message =
      error.statusmessage ||
      error.statusMessage ||
      (typeof error.response === 'string'
        ? error.response
        : null) ||
      error.response?.data?.response ||
      error.data?.response ||
      (
        error.message &&
        !error.message.includes(
          'Request failed with status code',
        )
          ? error.message
          : null
      ) ||
      error.error ||
      error.err ||
      error.msg ||
      error.detail ||
      error.description ||
      (
        error.data &&
        (
          error.data.message ||
          error.data.statusmessage ||
          error.data.error ||
          error.data.response ||
          error.data.statusMessage
        )
      ) ||
      (
        error.response &&
        error.response.data &&
        (
          error.response.data.message ||
          error.response.data.statusmessage ||
          error.response.data.error ||
          error.response.data.response ||
          error.response.data.statusMessage
        )
      ) ||
      (
        error.payload &&
        (
          error.payload.data?.message ||
          error.payload.data?.statusmessage ||
          error.payload.data?.error ||
          error.payload.data?.response ||
          error.payload.data?.statusMessage
        )
      ) ||
      (
        error.payload &&
        (
          error.payload.message ||
          error.payload.statusmessage ||
          error.payload.error ||
          error.payload.response ||
          error.payload.statusMessage
        )
      );

    if (
      message &&
      typeof message === 'string' &&
      message.trim()
    ) {
      const trimmedMessage = message.trim();

      // Catch network-related strings that arrive nested in Redux/Axios payloads
      if (
        trimmedMessage === 'Network Error' ||
        trimmedMessage.includes('ERR_NETWORK') ||
        trimmedMessage.includes('Failed to fetch') ||
        trimmedMessage.includes('Network request failed')
      ) {
        return 'Unable to connect to CloudVantage. Please check your internet connection and try again.';
      }

      return trimmedMessage;
    }

    // PRIORITY 6:
    // Validation errors under an "errors" key
    if (
      error.errors &&
      typeof error.errors === 'object'
    ) {
      const errorKeys =
        Object.keys(error.errors);

      if (errorKeys.length > 0) {
        const firstError =
          error.errors[errorKeys[0]];

        if (
          Array.isArray(firstError) &&
          firstError.length > 0
        ) {
          return firstError[0];
        }

        if (
          typeof firstError === 'string'
        ) {
          return firstError;
        }
      }
    }

    // PRIORITY 7:
    // Final fallback - inspect remaining keys
    const allKeys = Object.keys(error);

    const skipKeys = [
      'statuscode',
      'statusmessage',
      'message',
      'error',
      'response',
      'data',
      'payload',
      'loading',
      'isAuthenticated',
      'request',
      'config',
      'code',
      'stack',
    ];

    for (const key of allKeys) {
      if (skipKeys.includes(key)) {
        continue;
      }

      const val = error[key];

      if (
        Array.isArray(val) &&
        val.length > 0 &&
        typeof val[0] === 'string'
      ) {
        return val[0];
      }

      if (
        typeof val === 'string' &&
        val.trim()
      ) {
        return val.trim();
      }
    }
  }

  return 'An unexpected error occurred';
};

/**
 * Utility to map API field errors directly to Formik errors
 * @param {Object} error - The error object from the API catch block
 * @param {Function} setErrors - Formik's setErrors function
 */
export const setFormikErrors = (error, setErrors) => {
  if (!error || !setErrors) return;

  const data = error.response?.data || error.data || error;
  if (typeof data !== 'object') return;

  const formikErrors = {};
  const skipKeys = [
    'statuscode',
    'statusmessage',
    'message',
    'error',
    'response',
    'data',
    'payload',
    'loading',
    'isAuthenticated',
  ];

  Object.keys(data).forEach((key) => {
    if (skipKeys.includes(key)) return;
    const val = data[key];
    if (Array.isArray(val) && val.length > 0) {
      formikErrors[key] = val[0];
    } else if (typeof val === 'string' && val.trim()) {
      formikErrors[key] = val.trim();
    }
  });

  if (Object.keys(formikErrors).length > 0) {
    setErrors(formikErrors);
  }
};

    // ======================================================
    // SINGLE GLOBAL TOAST
    // ======================================================

    const APP_TOAST_ID = 'cloudvantage-global-toast';

    const showSingleToast = (type, message) => {
      if (!message) return null;

      const formattedMessage =
        capitalizeString(message);

      // If a toast is already showing,
      // update it instead of creating another one.
      if (toast.isActive(APP_TOAST_ID)) {
        toast.update(APP_TOAST_ID, {
          render: formattedMessage,
          type,
          theme: 'colored',
          autoClose: 5000,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        return null;
      }

      toast[type](formattedMessage, {
        toastId: APP_TOAST_ID,
        theme: 'colored',
        autoClose: 5000,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      return null;
    };

    export const showSuccessMessage = (message) => {
      if (!message) return null;

      const msg =
        typeof message === 'string'
          ? message
          : extractMessage(message);

      if (!msg) return null;

      return showSingleToast(
        'success',
        msg,
      );
    };

    export const showErrorMessage = (message) => {
      if (!message) return null;

      const msg =
        typeof message === 'string'
          ? message
          : extractErrorMessage(message);

      if (!msg) return null;

      return showSingleToast(
        'error',
        msg,
      );
    };


// Helper function to extract message from API response (for success messages)
const extractMessage = (response) => {
  if (!response) return null;

  if (typeof response === 'string') {
    return response.trim() || null;
  }

  if (typeof response === 'object') {
    // Check for statusmessage (app's standard format)
    if (
      response.statusmessage &&
      typeof response.statusmessage === 'string' &&
      response.statusmessage.trim()
    ) {
      return response.statusmessage.trim();
    }

    // Check nested data
    if (
      response.data &&
      response.data.statusmessage &&
      typeof response.data.statusmessage === 'string' &&
      response.data.statusmessage.trim()
    ) {
      return response.data.statusmessage.trim();
    }

    // Check response.data
    if (
      response.response &&
      response.response.data &&
      response.response.data.statusmessage &&
      typeof response.response.data.statusmessage === 'string' &&
      response.response.data.statusmessage.trim()
    ) {
      return response.response.data.statusmessage.trim();
    }

    // Fallback to common message properties
    if (
      response.message &&
      typeof response.message === 'string' &&
      response.message.trim()
    ) {
      return response.message.trim();
    }
  }

  return null;
};

// Universal function to handle API responses based on statuscode
// This automatically shows success/error messages based on statuscode
export const handleApiResponse = (response, options = {}) => {
  if (!response) {
    if (options.showError !== false) {
      showErrorMessage('An unexpected error occurred');
    }
    return { success: false, message: 'An unexpected error occurred' };
  }

  // Extract statuscode and statusmessage
  let statuscode = null;
  let statusmessage = null;

  // Check direct response
  if (response.statuscode !== undefined) {
    statuscode = response.statuscode;
    statusmessage = response.statusmessage;
  }
  // Check nested data
  else if (response.data && response.data.statuscode !== undefined) {
    statuscode = response.data.statuscode;
    statusmessage = response.data.statusmessage;
  }
  // Check response.response.data
  else if (
    response.response &&
    response.response.data &&
    response.response.data.statuscode !== undefined
  ) {
    statuscode = response.response.data.statuscode;
    statusmessage = response.response.data.statusmessage;
  }

  // Handle based on statuscode
  if (statuscode === '00') {
    // Success
    if (statusmessage && options.showSuccess !== false) {
      showSuccessMessage(statusmessage);
    }
    return {
      success: true,
      message: statusmessage || 'Success',
      data: response.data || response,
    };
  } else if (statuscode === '01' || statuscode !== null) {
    // Error (01 or any other statuscode)
    const errorMsg =
      statusmessage || extractErrorMessage(response) || 'An error occurred';
    if (options.showError !== false) {
      showErrorMessage(errorMsg);
    }
    return {
      success: false,
      message: errorMsg,
      data: response.data || response,
    };
  }

  // If no statuscode found, try to extract message anyway
  const message =
    extractMessage(response) ||
    extractErrorMessage(response) ||
    'An unexpected response received';
  if (options.showError !== false) {
    showErrorMessage(message);
  }
  return { success: false, message };
};

const capitalizeString = (string) => {
  if (!string || typeof string !== 'string') return string || '';
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const retrieveFromLocalStorage = (keys) => {
  const data = {};
  keys.forEach((key) => {
    const persistedState = localStorage.getItem(key);
    if (persistedState) {
      try {
        const bytes = CryptoJS.AES.decrypt(persistedState, APP_SECRET_KEY);
        const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
        data[key] = decryptedData ? JSON.parse(decryptedData) : null;
        if (typeof data[key] === 'string') {
          data[key] = JSON.parse(data[key]);
        }
      } catch (error) {
        data[key] = null;
      }
    } else {
      data[key] = null;
    }
  });
  return data;
};

export const convertAmountToNumber = (amountString) => {
  if (typeof amountString !== 'string') {
    throw new Error('Input must be a string');
  }
  const amountNumber = parseFloat(amountString.replace(/,/g, ''));

  if (isNaN(amountNumber)) {
    throw new Error('Invalid amount string');
  }

  return amountNumber;
};

<i class="fas fa-file-export    "></i>;

export const FormatDate = (dateString) => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const formatMonthYear = (dateKey) => {
  // If already formatted, return as is
  if (dateKey?.includes(' ')) return dateKey;

  // Only try to format if it's in YYYY-MM format
  if (dateKey?.match(/^\d{4}-\d{2}$/)) {
    const [year, month] = dateKey.split('-');
    const date = new Date(`${year}-${month}-01`);
    return date.toLocaleString('en-US', {
      month: 'long',
      year: 'numeric',
    });
  }

  return ''; // Return empty string for invalid formats
};

export function formatDateTime(isoString) {
  const date = new Date(isoString);

  // Extract date components
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();

  // Format month
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const month = monthNames[date.getUTCMonth()];

  // Format time
  let hours = date.getUTCHours();
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const period = hours >= 12 ? 'pm' : 'am';

  // Convert to 12-hour format
  hours = hours % 12 || 12;

  return `${day} ${month}, ${year} at ${hours}:${minutes}${period}`;
}

export function extractLastNumbers(uuid) {
  return uuid.slice(0, 13);
}

export function formatDateMethod(dateString) {
  const date = new Date(dateString);

  const day = date.getDate().toString().padStart(2, '0');
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  // Get hours, minutes, and AM/PM
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  const time = `${hours}:${minutes} ${ampm}`;

  return `${day} ${month}, ${year} at ${time}`;
}

export const isDevelopment = () => {
  const hostname = window.location.hostname;
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('192.168.') ||
    hostname.startsWith('10.') ||
    hostname.endsWith('.local')
  );
};

export function extractHostAndPort(url) {
  try {
    const parsedUrl = new URL(url);
    return `${parsedUrl.hostname}`;
  } catch (error) {
    console.error('Invalid URL:', error.message);
    return null;
  }
}

export const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = (error) => reject(error);
  });

export const capitalizeFirstLetter = (string) => {
  if (!string) return 'Unfulfilled';
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

export function inferExtFromMime(mime) {
  const map = {
    'application/pdf': 'pdf',
    'application/zip': 'zip',
    'application/json': 'json',
    'text/plain': 'txt',
    'text/csv': 'csv',
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      'docx',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      'pptx',
  };
  return map[mime] || null;
}

export const checkIsOffice = (mime) => {
  const isOffice =
    mime ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || // docx
    mime ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || // xlsx
    mime ===
      'application/vnd.openxmlformats-officedocument.presentationml.presentation' || // pptx
    mime === 'application/msword' || // doc (legacy)
    mime === 'application/vnd.ms-excel' || // xls (legacy)
    mime === 'application/vnd.ms-powerpoint'; // ppt (legacy)
  return isOffice;
};

export const isFolder = (name) => name.endsWith('/');

// In Use in src/pages/users/pages/bucketStorages.js
export function buildGroups(items) {
  const folderMap = new Map();
  const rows = [];

  // Register folders
  for (const it of items) {
    if (isFolder(it.key) || it.name) {
      const key = it.key || it.name;
      folderMap.set(key, { folder: it, children: [] });
    }
  }

  // Distribute files
  for (const it of items) {
    const key = it.key || it.name;
    if (isFolder(key)) continue;
    const parent = Array.from(folderMap.keys()).find((fk) =>
      key.startsWith(fk),
    );
    if (parent) {
      folderMap.get(parent).children.push(it);
    } else {
      rows.push(it);
    }
  }

  // Folders (with children) + loose files
  for (const group of folderMap.values()) {
    rows.push({ ...group.folder, children: group.children });
  }

  // Folders first, then files by name
  rows.sort((a, b) => {
    const aF = isFolder(a.name || a.key);
    const bF = isFolder(b.name || b.key);
    if (aF !== bF) return aF ? -1 : 1;
    return (a.name || a.key).localeCompare(b.name || b.key);
  });

  return rows;
}

export function extractFolders(objects) {
  if (!Array.isArray(objects)) return [];

  return objects
    .filter((o) => o.isFolder)
    .map((o) => ({
      name: o.key.replace(/\/$/, ''), // strip trailing slash for display
      size: o.size ?? '0.0 MB', // default if backend uses 0-size for folders
    }));
}
// export function extractHostAndPort(url) {
//   try {
//     const parsedUrl = new URL(url);
//     return `${parsedUrl.hostname}`;
//   } catch (error) {
//     console.error("Invalid URL:", error.message);
//     return null;
//   }
// }

// export const fileToBase64 = (file) =>
//   new Promise((resolve, reject) => {
//     const reader = new FileReader();
//     reader.readAsDataURL(file);
//     reader.onload = () => resolve(reader.result.split(",")[1]);
//     reader.onerror = (error) => reject(error);
//   });
